// Treatment types based on OpenAPI schema and legacy system patterns

// Treatment type enum - matches legacy system
export enum TreatmentType {
  Drug = 1,
  Procedure = 2,
  OtherTreatment = 3,
}

// Source reference
export interface Source {
  id: number;
  name: string;
}

// Drug substance from treatment
export interface DrugSubstance {
  id: number;
  name: string;
  has_doses?: boolean;
}

// Procedure type
export interface ProcedureType {
  id: number;
  name: string;
}

// Procedure from treatment
export interface Procedure {
  id: number;
  name: string;
  procedure_type?: ProcedureType;
}

// Treatment optionality (mandatory, optional, etc.)
export interface TreatmentOptionality {
  id: number;
  name: string;
}

// Treatment set (protocol grouping)
export interface TreatmentSet {
  id: number;
  name: string;
  order: number;
}

// Treatment from API
export interface Treatment {
  id: number;
  type: TreatmentType;
  info: string;
  follow_up?: string;
  procedure?: Procedure;
  drug_substance?: DrugSubstance;
  treatment_optionality?: TreatmentOptionality;
}

// Calculated dosage data stored with treatment
export interface CalculatedDosageData {
  // The calculation method used
  calculation_type: 'numberOfUnits' | 'drugAmount' | 'drugStrength';
  // Weight used for calculation
  weight_kg: number;
  // Drug strength (e.g., "100" mg/ml)
  strength: string;
  // Unit type (mg/ml, mg/tablet, mg/capsule, ml, tablet(s), capsule(s))
  unit: number;
  // Strength unit label (e.g., "mg/ml", "mg/tablet")
  strength_unit?: string;
  // Amount unit label (e.g., "ml", "tablet(s)")
  amount_unit?: string;
  // Dose value (mg/kg)
  dose: string;
  // Calculated result
  result: string;
  // Selected dose alternative info
  dose_alternative_id?: number;
  dose_indication_name?: string;
  dose_type_name?: string;
  administration_route_name?: string;
}

// Selected treatment stored in case
export interface SelectedTreatment {
  treatment_id: number;
  disease_id: number;
  treatment_set_id: number;
  type: TreatmentType;
  name: string; // Cached name for display
  // Optional calculated dosage for drug treatments
  calculated_dosage?: CalculatedDosageData;
}

// Treatment info combined with disease data
export interface TreatmentInfo {
  diseaseId: number;
  diseaseName: string;
  treatmentSets: TreatmentSet[];
  treatments: Map<number, Treatment[]>; // Map of treatmentSetId -> treatments
}

// Helper function to get treatment display name
export const getTreatmentDisplayName = (treatment: Treatment): string => {
  switch (treatment.type) {
    case TreatmentType.Drug:
      return treatment.drug_substance?.name || 'Unknown drug';
    case TreatmentType.Procedure:
      return treatment.procedure?.name || 'Unknown procedure';
    case TreatmentType.OtherTreatment:
      return treatment.info || 'Other treatment';
    default:
      return 'Unknown treatment';
  }
};

// Helper function to get treatment type label
export const getTreatmentTypeLabel = (type: TreatmentType): string => {
  switch (type) {
    case TreatmentType.Drug:
      return 'Medication';
    case TreatmentType.Procedure:
      return 'Procedure';
    case TreatmentType.OtherTreatment:
      return 'Other';
    default:
      return 'Unknown';
  }
};
